import {useMemo} from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import {InputField} from '@/pages/components/common';
import {AuditProgramSectionVO} from '@/services/auditService/auditProgramService';
import type {S3Page} from '@/pages/components/audit-program/s-section/constants';
import {S3_PAGE} from '@/pages/components/audit-program/s-section/constants';
import ProgramSectionHeader from '@/pages/components/audit-program/a-section/ProgramSectionHeader';

export interface S3IceViewProps {
    header: AuditProgramSectionVO;
    page: S3Page;
    refCode?: string;
    values: Record<string, string>;
    onChange: (key: string, value: string) => void;
}

const cellBorder = {borderColor: 'divider'};
const headSx = {...cellBorder, fontWeight: 600, fontSize: '0.7rem', py: 0.6, px: 0.35, lineHeight: 1.25};
const bodySx = {...cellBorder, py: 0.35, px: 0.3, verticalAlign: 'top' as const, overflow: 'hidden'};
const fieldSx = {minWidth: 0, width: '100%', maxWidth: '100%'};

/** S3 — Internal Control Evaluation blank working table. */
export default function S3IceView({
    header,
    page,
    refCode = 'S3',
    values,
    onChange,
}: S3IceViewProps) {
    const meta = {
        title: page.title || S3_PAGE.title,
        blankRows: page.blankRows || S3_PAGE.blankRows,
        columns: page.columns?.length ? page.columns : S3_PAGE.columns,
    };
    const rowIds = useMemo(
        () => Array.from({length: Math.max(1, meta.blankRows)}, (_, i) => `r${i + 1}`),
        [meta.blankRows],
    );

    return (
        <Box sx={{p: 2}}>
            <ProgramSectionHeader
                header={header}
                variant="a12"
                refCode={refCode}
                values={values}
                onChange={onChange}
            />

            <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{textAlign: 'center', letterSpacing: 0.3, mb: 1.5, mt: 0.5}}
            >
                {meta.title}
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{mb: 1.5}}>
                Document each control to be tested and reference working papers evidencing the testing of
                operating effectiveness. Inquiry alone is not sufficient.
            </Typography>

            <TableContainer sx={{mb: 2, overflowX: 'auto'}}>
                <Table size="small" sx={{minWidth: 960, tableLayout: 'fixed', borderCollapse: 'collapse'}}>
                    <colgroup>
                        <col style={{width: 32}}/>
                        <col style={{width: 72}}/>
                        <col style={{width: 140}}/>
                        <col/>
                        <col/>
                        <col style={{width: 120}}/>
                    </colgroup>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={headSx}/>
                            {meta.columns.map((c) => (
                                <TableCell key={c.id} sx={headSx}>{c.label}</TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rowIds.map((id, idx) => (
                            <TableRow key={id}>
                                <TableCell sx={bodySx}>
                                    <Typography variant="body2" color="text.secondary">{idx + 1}</Typography>
                                </TableCell>
                                {meta.columns.map((c) => (
                                    <TableCell key={c.id} sx={bodySx}>
                                        <InputField
                                            type="text"
                                            fullWidth
                                            compact
                                            multiline={c.id === 'keyControl' || c.id === 'test'}
                                            minRows={c.id === 'keyControl' || c.id === 'test' ? 2 : 1}
                                            sx={fieldSx}
                                            value={values[`${id}.${c.id}`] ?? ''}
                                            onChange={(e) => onChange(`${id}.${c.id}`, e.target.value)}
                                        />
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}
